/**
 * Read-only client for the live-wind Worker's public /v1/stations API (SRF-2855).
 *
 * The admin browser talks to the Worker directly (CORS-allowlisted origin), never through
 * the classic backend. Two calls only: candidates near a spot, and validation of one id.
 */
import { ENDPOINTS } from "@/constants/api";

export interface LiveWindStation {
  id: string;
  name: string;
  lat: number;
  lon: number;
  source: string;
  windKts: number | null;
  windDir: number | null;
  gustKts: number | null;
  obsTs: number | null;
  stale: boolean;
  attribution?: { label: string; url?: string };
  distanceKm?: number;
}

export type StationLookup =
  | { status: "found"; station: LiveWindStation }
  | { status: "not_found" }
  | { status: "invalid_id" }
  | { status: "unreachable"; message: string };

/** Mirrors the Worker's id gate so obviously bad input never becomes a request. */
export const STATION_ID_RE = /^[a-z]+_[A-Za-z0-9_.-]{1,64}$/;
export const isValidStationId = (id: string): boolean => id.length <= 64 && STATION_ID_RE.test(id);

const TIMEOUT_MS = 5000;

async function fetchJson(url: string): Promise<{ ok: boolean; status: number; body: any }> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(url, { headers: { Accept: "application/json" }, signal: controller.signal });
    let body: any = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    return { ok: res.ok, status: res.status, body };
  } finally {
    clearTimeout(timer);
  }
}

export class LiveWindStationService {
  /** Validate one id against the Worker — the only way a typed id may be saved. */
  static async lookup(id: string): Promise<StationLookup> {
    const trimmed = id.trim();
    if (!isValidStationId(trimmed)) return { status: "invalid_id" };
    try {
      const r = await fetchJson(ENDPOINTS.LIVE_WIND.STATION(trimmed));
      if (r.ok && r.body && r.body.id === trimmed) return { status: "found", station: r.body as LiveWindStation };
      if (r.status === 404) return { status: "not_found" };
      if (r.status === 400) return { status: "invalid_id" };
      return { status: "unreachable", message: `live-wind service answered ${r.status}` };
    } catch (e: any) {
      return { status: "unreachable", message: e?.name === "AbortError" ? "live-wind service timed out" : "live-wind service unreachable" };
    }
  }

  /** Up to `limit` stations closest to the spot, with their current reading. */
  static async nearest(lat: number, lon: number, limit = 10): Promise<LiveWindStation[] | null> {
    try {
      const r = await fetchJson(ENDPOINTS.LIVE_WIND.NEAREST(lat, lon, limit));
      if (!r.ok || !Array.isArray(r.body?.stations)) return null;
      return r.body.stations as LiveWindStation[];
    } catch {
      return null;
    }
  }
}
